import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated, getAuthConfig, logout } from '../utils/auth';
import AppNav from '../components/AppNav';
import './AccessDetailsPage.css';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function AccessDetailsPage() {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  useEffect(() => {
    if (!isAuthenticated()) { navigate(`/login?next=/subscriptions/${subscriptionId}/access`, { replace: true }); return; }
    loadAccess();
  }, [subscriptionId]);

  const loadAccess = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/api/subscriptions/${subscriptionId}/access`, getAuthConfig());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load access details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (loading) return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="loading-container"><div className="loading-spinner"></div></div>
      <p className="loading-text">Loading access details...</p>
    </div>
  );

  if (error) return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="access-page">
        <div className="alert alert-error">{error}</div>
        <button className="sub-btn sub-btn--outline" onClick={() => navigate('/subscriptions')}>Back to Subscriptions</button>
      </div>
    </div>
  );

  const sub = data?.subscription || {};
  const fields = data?.fields || [];
  const enabledFields = fields.filter(f => !f.locked && f.enabled);
  const disabledFields = fields.filter(f => !f.locked && !f.enabled);
  const lockedFields = fields.filter(f => f.locked);

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="access-page">
        <button className="access-back-btn" onClick={() => navigate('/subscriptions')}>
          <i className="fa-solid fa-arrow-left"></i> Back to Subscriptions
        </button>

        <div className="access-header">
          <div className="access-header-icon">{(sub.serviceName || 'S').charAt(0)}</div>
          <div>
            <h1 className="access-header-title">{sub.planName}</h1>
            <div className="access-header-meta">
              <span>{sub.serviceName}</span>
              {sub.domain && <><span className="access-meta-sep">/</span><span>{sub.domain}</span></>}
            </div>
          </div>
        </div>

        {sub.serviceStatus !== 'active' && (
          <div className="access-notice access-notice--pending">
            <i className="fa-solid fa-hourglass-half"></i>
            Your service is being set up. Access details will appear once activated.
          </div>
        )}

        {enabledFields.length > 0 && (
          <div className="access-section">
            <h2 className="access-section-title">Your Access</h2>
            <div className="access-fields-list">
              {enabledFields.map(f => (
                <div key={f.field_key} className="access-field-card">
                  <div className="access-field-label">{f.field_label}</div>
                  <div className="access-field-value-row">
                    {f.field_type === 'url' && f.field_value ? (
                      <a href={f.field_value.match(/^https?:\/\//) ? f.field_value : 'https://' + f.field_value} target="_blank" rel="noopener noreferrer" className="access-field-link">
                        {f.field_value}
                        <i className="fa-solid fa-arrow-up-right-from-square"></i>
                      </a>
                    ) : f.field_type === 'textarea' && f.field_value ? (
                      <span className="access-field-text" style={{ whiteSpace: 'pre-wrap' }}>{f.field_value}</span>
                    ) : (
                      <span className="access-field-text">{f.field_value || '—'}</span>
                    )}
                    {f.field_value && (
                      <button className="access-copy-btn" onClick={() => handleCopy(f.field_key, f.field_value)} title="Copy">
                        <i className={`fa-solid ${copiedKey === f.field_key ? 'fa-check' : 'fa-copy'}`}></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {disabledFields.length > 0 && (
          <div className="access-section">
            <h2 className="access-section-title" style={{ color: '#888' }}>Pending Setup</h2>
            <div className="access-fields-list">
              {disabledFields.map(f => (
                <div key={f.field_key} className="access-field-card access-field-card--disabled">
                  <div className="access-field-label">{f.field_label}</div>
                  <div className="access-field-value-row">
                    <span className="access-field-text" style={{ color: '#555' }}>Awaiting setup by our team</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {lockedFields.length > 0 && (
          <div className="access-section">
            <h2 className="access-section-title" style={{ color: '#666' }}>
              <i className="fa-solid fa-lock" style={{ marginRight: 8, fontSize: 14 }}></i>
              Available on Higher Plans
            </h2>
            <div className="access-fields-list">
              {lockedFields.map(f => (
                <div key={f.field_key} className="access-field-card access-field-card--locked">
                  <div className="access-field-locked-row">
                    <div>
                      <div className="access-field-label">{f.field_label}</div>
                      <div className="access-field-locked-plan">
                        Available on <strong>{f.available_on_plan}</strong>
                      </div>
                    </div>
                    <Link to={`/${sub.service}`} className="access-upgrade-btn">
                      Upgrade <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {enabledFields.length === 0 && disabledFields.length === 0 && lockedFields.length === 0 && (
          <div className="access-empty">
            <i className="fa-solid fa-info-circle"></i>
            <p>No access fields have been configured for this plan yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
